import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import BackToTop from "@/components/feature/BackToTop";
import ComicCard from "@/components/feature/ComicCard";
import { categories, comics } from "@/mocks/comics";

const typeMap: Record<string, { name: string; icon: string; desc: string }> = {
  mengxi: { name: "萌系漫画", icon: "ri-heart-2-line", desc: "软萌角色、可爱画风，治愈你的每一天" },
  tianchong: { name: "甜宠漫画", icon: "ri-cake-3-line", desc: "高甜预警，宠溺日常甜到齁" },
  lianai: { name: "恋爱漫画", icon: "ri-flower-line", desc: "怦然心动的少女恋爱物语" },
  all: { name: "全部漫画", icon: "ri-apps-2-line", desc: "浏览可可漫画网全部萌系、甜宠与恋爱漫画" },
};

export default function CategoryPage() {
  const { type = "all" } = useParams();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get("q") || "").trim();
  const meta = typeMap[type] || typeMap.all;

  const filtered = useMemo(() => {
    let list = comics;
    if (type !== "all") {
      list = list.filter((c) => c.category === meta.name);
    }
    if (q) {
      const kw = q.toLowerCase();
      list = list.filter(
        (c) =>
          c.title.toLowerCase().includes(kw) ||
          c.author.toLowerCase().includes(kw) ||
          c.tags.some((t) => t.toLowerCase().includes(kw)),
      );
    }
    return list;
  }, [type, meta.name, q]);

  const sortOptions = ["按热度", "按评分", "最新更新"];
  const [activeSort, setActiveSort] = useState("按热度");

  const sorted = useMemo(() => {
    const arr = [...filtered];
    if (activeSort === "按评分") return arr.sort((a, b) => b.rating - a.rating);
    if (activeSort === "最新更新") return arr.sort((a, b) => b.episodes - a.episodes);
    return arr.sort((a, b) => b.views - a.views);
  }, [filtered, activeSort]);

  return (
    <div className="bg-background-50">
      <Navbar />
      <main className="pt-16">
        <section className="bg-gradient-to-b from-primary-100 to-background-50 py-12 md:py-14">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <nav aria-label="面包屑" className="mb-5 text-sm text-foreground-500">
              <ol className="flex flex-wrap items-center gap-1.5">
                <li>
                  <Link to="/" className="hover:text-primary-600">首页</Link>
                </li>
                <li aria-hidden="true"><i className="ri-arrow-right-s-line" /></li>
                <li aria-current="page" className="text-foreground-800">{meta.name}</li>
              </ol>
            </nav>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-primary-500 text-background-50">
                <i className={`${meta.icon} text-3xl`} />
              </span>
              <div>
                <h1 className="font-heading text-3xl text-foreground-900">{meta.name}</h1>
                <p className="mt-1.5 text-sm text-foreground-500">{meta.desc}</p>
              </div>
              <div className="mt-2 sm:ml-auto">
                <span className="rounded-full bg-background-100 px-4 py-2 text-sm text-foreground-600">
                  共 {filtered.length} 部
                </span>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {categories.map((c) => {
                const active = type === c.key;
                return (
                  <Link
                    key={c.key}
                    to={`/category/${c.key}`}
                    className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                      active
                        ? "bg-primary-500 text-background-50"
                        : "bg-background-100 text-foreground-600 hover:bg-primary-100"
                    }`}
                  >
                    {c.name}
                  </Link>
                );
              })}
              <Link
                to="/category/all"
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm transition-colors ${
                  type === "all"
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-100 text-foreground-600 hover:bg-primary-100"
                }`}
              >
                全部
              </Link>
            </div>
          </div>
        </section>

        <section className="py-10">
          <div className="mx-auto max-w-7xl px-4 md:px-6">
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-foreground-500">
                {q ? (
                  <>
                    搜索“<strong className="text-primary-600">{q}</strong>”找到 {filtered.length} 部漫画
                  </>
                ) : (
                  <>为你推荐 {sorted.length} 部精彩漫画</>
                )}
              </p>
              <div className="flex items-center gap-1 rounded-full bg-background-100 p-1">
                {sortOptions.map((opt) => (
                  <button
                    key={opt}
                    onClick={() => setActiveSort(opt)}
                    className={`whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      activeSort === opt ? "bg-primary-500 text-background-50" : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {opt}
                  </button>
                ))}
              </div>
            </div>

            {sorted.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-4 rounded-2xl bg-background-100 py-20 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-400">
                  <i className="ri-search-line text-3xl" />
                </span>
                <div>
                  <h2 className="text-lg font-medium text-foreground-900">没有找到相关漫画</h2>
                  <p className="mt-1 text-sm text-foreground-500">换个关键词试试，或回到分类浏览全部作品</p>
                </div>
                <Link to="/category/all" className="whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm text-background-50 hover:bg-primary-600">
                  浏览全部
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5 lg:grid-cols-4 xl:grid-cols-5">
                {sorted.map((c) => (
                  <ComicCard key={c.id} comic={c} />
                ))}
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `${meta.name} - 可可漫画网`,
            url: `https://adorabook.github.io/category/${type}`,
            description: meta.desc,
            mainEntity: {
              "@type": "ItemList",
              itemListElement: sorted.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.title,
                url: `https://adorabook.github.io/comic/${c.id}`,
              })),
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "首页", item: "https://adorabook.github.io/" },
                { "@type": "ListItem", position: 2, name: meta.name },
              ],
            },
          }),
        }}
      />
    </div>
  );
}